import React from 'react';

import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { Info } from 'lucide-react';

import { customPalette } from '@shared/theme/StyleProvider';

type MetricItemProps = {
  icon: React.ReactNode;
  value: string | number | undefined;
  label: string;
  loading?: boolean;
  tooltip?: string | { title: string; description: React.ReactNode };
};

const renderTooltipContent = (tooltip: MetricItemProps['tooltip']) => {
  if (!tooltip) {
    return null;
  }
  if (typeof tooltip === 'string') {
    return tooltip;
  }
  return (
    <Stack spacing={0.5}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
        }}
      >
        {tooltip.title}
      </Typography>
      <Typography variant="body2" component="div">
        {tooltip.description}
      </Typography>
    </Stack>
  );
};

const MetricItem = ({
  icon,
  value,
  label,
  loading = false,
  tooltip,
}: MetricItemProps) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        {loading ? (
          <Skeleton width={70} height={26} />
        ) : (
          <Typography
            sx={{ fontSize: '1.375rem', fontWeight: 800, lineHeight: 1.1 }}
          >
            {value ?? 'N/A'}
          </Typography>
        )}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.375, mt: 0.25 }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 500, color: 'text.secondary', lineHeight: 1.2 }}
          >
            {label}
          </Typography>
          {tooltip && (
            <Tooltip
              arrow
              placement="top"
              slotProps={{
                tooltip: { sx: { maxWidth: 280, px: 1.5, py: 1 } },
              }}
              title={renderTooltipContent(tooltip)}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  color: customPalette.gray400,
                  cursor: 'help',
                }}
              >
                <Info size={12} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MetricItem;
